import type React from "react";
import { useEffect, useState } from "react";

import { faCircleDollar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Slider, SliderMark, SliderMarkLabel, Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { useGetCommissionRates, usePostCommissionRates } from "api/services/admin/hooks";
import type { CommissionRatePayload } from "api/services/admin/schemas";
import { BaseButton } from "shared/atoms/BaseButton";
import { WidgetBox } from "shared/containers/WidgetBox";
import { GREEN, PURPLE } from "theme/colors";
import { reportError } from "utils/errorReporting";

import { SummaryBox } from "../EnrollmentGoals/SummaryBox";

import { CommissionRatesSkeleton } from "./CommissionRatesSkeleton";

interface CommissionRatesProps {
  hcoID: number;
}

export const CommissionRates: React.FC<CommissionRatesProps> = ({ hcoID }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [showSaved, setShowSaved] = useState(false);
  const postCommissionRates = usePostCommissionRates();

  const {
    data: commissionRatesData,
    isLoading,
    refetch,
  } = useGetCommissionRates({ health_care_organization_id: hcoID });

  const form = useForm<CommissionRatePayload>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      commission_bps: 0,
    },
  });

  useEffect(() => {
    if (commissionRatesData?.current?.commission_bps !== undefined) {
      form.reset({
        commission_bps: commissionRatesData.current.commission_bps,
      });
    }
  }, [commissionRatesData, form]);

  const onSubmit = form.handleSubmit(async formData => {
    try {
      await postCommissionRates.mutateAsync({
        health_care_organization_id: hcoID,
        payload: formData,
      });
      await refetch();
      form.reset(formData);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
    } catch (error) {
      reportError("CommissionRates: onSubmit failed", new Error(JSON.stringify({ error, variables: formData })));
    }
  });

  const savedCommissionRate =
    commissionRatesData?.current?.commission_bps !== undefined ? commissionRatesData.current.commission_bps : 0;
  const savedCommissionRatePercentage = savedCommissionRate / 100;
  const watchedCommissionRate = form.watch("commission_bps");

  const marks = [
    ...[
      { value: 10, label: "10%" },
      { value: 25, label: "25%" },
      { value: 50, label: "50%" },
      { value: 75, label: "75%" },
    ].map(mark =>
      savedCommissionRate && Math.abs(mark.value - savedCommissionRatePercentage) <= 5 ? { ...mark, label: "" } : mark
    ),
    ...(savedCommissionRate
      ? [
          {
            value: savedCommissionRatePercentage,
            label: t("admin.commission_rates.form.current_rate", { rate: savedCommissionRatePercentage }),
          },
        ]
      : []),
  ];

  return (
    <WidgetBox
      title={t("admin.commission_rates.title")}
      icon={<FontAwesomeIcon icon={faCircleDollar} />}
      toolbar={
        <Stack direction="row" spacing={1} alignItems="center">
          {form.formState.isDirty && (
            <>
              <BaseButton
                type="submit"
                form="commission-rates-form"
                disabled={postCommissionRates.isLoading}
                text={t("buttons.save")}
                size="small"
                square
                style={{ flexBasis: "auto" }}
              />
              <BaseButton
                text={t("buttons.discard")}
                variant="tertiary"
                disabled={postCommissionRates.isLoading}
                onClick={() => form.reset({ commission_bps: savedCommissionRate })}
                size="small"
                square
                style={{ flexBasis: "auto" }}
              />
            </>
          )}

          {!form.formState.isDirty && showSaved && (
            <SummaryBox backgroundColor={GREEN[10]}>
              <Typography variant="subtitle2" color={GREEN[100]}>
                {t("buttons.saved")}
              </Typography>
            </SummaryBox>
          )}
        </Stack>
      }
      color="purple"
      skipPadding
    >
      {isLoading && <CommissionRatesSkeleton />}
      {!isLoading && (
        <FormProvider {...form}>
          <form id="commission-rates-form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="subtitle2">{t("admin.commission_rates.form.label")}</Typography>
                <SummaryBox backgroundColor={PURPLE[10]}>
                  <Typography variant="subtitle2" color={PURPLE[100]}>
                    {watchedCommissionRate / 100}%
                  </Typography>
                </SummaryBox>
              </Stack>

              <Controller
                name="commission_bps"
                control={form.control}
                render={({ field }) => (
                  <Slider
                    value={field.value / 100}
                    onChange={(_, value) => field.onChange((value as number) * 100)}
                    onBlur={field.onBlur}
                    min={0}
                    max={100}
                    marks={marks}
                    valueLabelDisplay="auto"
                    valueLabelFormat={value => `${value}%`}
                    slots={{
                      mark: props => {
                        const markValue = marks[props["data-index"] as number]?.value;
                        return (
                          <SliderMark
                            {...props}
                            className={clsx(props.className, {
                              "is-current-rate": markValue === savedCommissionRatePercentage,
                            })}
                          />
                        );
                      },
                      markLabel: props => {
                        const markValue = marks[props["data-index"] as number]?.value;
                        return (
                          <SliderMarkLabel
                            {...props}
                            className={clsx(props.className, {
                              "is-current-rate": markValue === savedCommissionRatePercentage,
                            })}
                          />
                        );
                      },
                    }}
                    sx={{
                      color: theme.colors.redesign.p100,
                      "& .MuiSlider-thumb": { width: 20, height: 20 },
                      "& .MuiSlider-rail": { opacity: 0.15, height: 8 },
                      "& .MuiSlider-track": { height: 8 },
                      "& .MuiSlider-markLabel": {
                        fontSize: 12,
                        color: theme.colors.redesign.db40,
                        "&.is-current-rate": {
                          fontWeight: 600,
                          color: theme.colors.redesign.db60,
                        },
                      },
                      "& .MuiSlider-mark": {
                        height: 10,
                        width: 2,
                        borderRadius: 1,
                        "&.is-current-rate": {
                          width: 4,
                          height: 20,
                          backgroundColor: theme.colors.redesign.p100,
                        },
                      },
                    }}
                  />
                )}
              />

              <SummaryBox label={t("admin.commission_rates.form.note")} />
            </Stack>
          </form>
        </FormProvider>
      )}
    </WidgetBox>
  );
};
